import React from 'react'
import { Modal, Form, Input, DatePicker, TimePicker, Checkbox, Button, Alert, message } from 'antd'
import { FormComponentProps } from 'antd/lib/form'
import moment from 'moment'
import lecturesService from '@services/mediwho-app'
import { SectionTitle, StepBar, renderWizardFooter, makeWizardHandlers, SendNotificationCheckbox } from '../shared/mediwhoAppStyles'

const LECTURE_STEPS = [
    { label: 'פרטים', desc: 'נושא ותאריך' },
    { label: 'מיקום', desc: 'כתובת והתראה' }
]

const LECTURE_STEP_FIELDS = [
    ['topic', 'date', 'time'],
    [] as string[]
]

interface Props extends FormComponentProps {
    visible: boolean
    lecture: any
    onClose: () => void
    // Modal always emits body — page owns create/update
    onSaved: (triggerNotification: boolean, lectureData: { topic: string; description: string }, body: any) => void
}

interface LectureState {
    saving: boolean
    step: number
    hasDigitalLink: boolean
    isPast: boolean
    sendNotification: boolean
    existingNotification: any
    savedValues: Record<string, any>
}

class LectureModal extends React.Component<Props, LectureState> {
    state: LectureState = {
        saving: false,
        step: 0,
        hasDigitalLink: false,
        isPast: false,
        sendNotification: false,
        existingNotification: null,
        savedValues: {}
    }

    // Shared wizard helpers — no more copy-paste across modals
    get wizard() {
        return makeWizardHandlers(
            this.props.form,
            LECTURE_STEP_FIELDS,
            () => this.state,
            updater => this.setState(updater)
        )
    }

    componentDidUpdate(prev: Props) {
        if (this.props.lecture !== prev.lecture || this.props.visible !== prev.visible) {
            const hdl = this.props.lecture && this.props.lecture.has_digital_link
            const isPast = !!(this.props.lecture && new Date(this.props.lecture.lecture_at) <= new Date())
            this.setState({ hasDigitalLink: !!hdl, isPast, sendNotification: false, existingNotification: null, step: 0, savedValues: {}, saving: false })
            this.props.form.resetFields()
            if (this.props.lecture && this.props.lecture.notification_ids) {
                this.fetchExistingNotifications(this.props.lecture.notification_ids)
            }
        }
    }

    fetchExistingNotifications = async (raw: any) => {
        try {
            var ids: number[] = Array.isArray(raw) ? raw : JSON.parse(raw || '[]')
            if (!ids.length) return
            const results = await Promise.all(ids.map((id: number) => lecturesService.getNotification(id)))
            this.setState({ existingNotification: results.map((r: any) => r.data).filter(Boolean) })
        } catch (e) {
            // ignore
        }
    }

    handleSave = () => {
        if (this.state.saving) return
        this.props.form.validateFields((err: any, values: any) => {
            if (err) return

            const merged = Object.assign({}, this.state.savedValues, values)
            const dt = merged.date
                .clone()
                .hour(merged.time.hour())
                .minute(merged.time.minute())
                .second(0)

            if (dt.isBefore(moment())) {
                this.props.form.setFields({
                    date: { value: merged.date, errors: [new Error('התאריך חייב להיות בעתיד')] }
                })
                this.setState({ step: 0 })
                return
            }

            const body = {
                topic: merged.topic,
                description: merged.description || null,
                lecture_at: dt.format('YYYY-MM-DD HH:mm:ss'),
                lecture_address: merged.lecture_address || null,
                registration_link: merged.registration_link || null,
                has_digital_link: this.state.hasDigitalLink ? 1 : 0,
                digital_link: this.state.hasDigitalLink ? merged.digital_link || null : null
            }

            const lectureData = { topic: merged.topic, description: merged.description || '' }
            this.setState({ saving: true })
            // Page always owns create/update — modal only emits
            this.props.onSaved(this.state.sendNotification, lectureData, body)
        })
    }

    renderStep0() {
        const { lecture, form: { getFieldDecorator } } = this.props
        const readonly = this.state.isPast
        const initialMoment = lecture ? moment(lecture.lecture_at) : undefined
        const getSavedFieldValue = this.wizard.getSavedFieldValue
        return (
            <div>
                <div className="mediwho-card">
                    <SectionTitle label="פרטי הרצאה" color="#1890ff" />
                    <Form.Item label="נושא" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('topic', {
                            initialValue: getSavedFieldValue('topic', lecture ? lecture.topic : ''),
                            rules: [{ required: true, message: 'נושא הוא שדה חובה' }]
                        })(<Input disabled={readonly} placeholder="הכנס נושא" className="mediwho-lecture-topic-input" />)}
                    </Form.Item>
                    <Form.Item label="תיאור" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('description', {
                            initialValue: getSavedFieldValue('description', lecture ? lecture.description : '')
                        })(<Input.TextArea rows={3} disabled={readonly} placeholder="הכנס תיאור" className="mediwho-lecture-description-input" />)}
                    </Form.Item>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <Form.Item label="תאריך" style={{ flex: 1, marginBottom: 12 }}>
                            {getFieldDecorator('date', {
                                initialValue: getSavedFieldValue('date', initialMoment),
                                rules: [{ required: true, message: 'תאריך הוא שדה חובה' }]
                            })(
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    placeholder="בחר תאריך"
                                    className="mediwho-lecture-date-input"
                                    disabled={readonly}
                                    disabledDate={(d: any) => d && d.isBefore(moment(), 'day')}
                                />
                            )}
                        </Form.Item>
                        <Form.Item label="שעה" style={{ flex: 1, marginBottom: 12, position: 'relative' }}>
                            {getFieldDecorator('time', {
                                initialValue: getSavedFieldValue('time', initialMoment),
                                rules: [{ required: true, message: 'שעה היא שדה חובה' }]
                            })(
                                <TimePicker
                                    style={{ width: '100%' }}
                                    format="HH:mm"
                                    placeholder="בחר שעה"
                                    className="mediwho-lecture-time-input"
                                    disabled={readonly}
                                    popupClassName="mediwho-app-time-popup"
                                    getPopupContainer={(trigger: any) => trigger.parentNode}
                                />
                            )}
                        </Form.Item>
                    </div>
                </div>
            </div>
        )
    }

    renderStep1() {
        const { lecture, form: { getFieldDecorator } } = this.props
        const { hasDigitalLink, isPast: readonly, sendNotification, existingNotification } = this.state
        const getSavedFieldValue = this.wizard.getSavedFieldValue
        return (
            <div>
                <div className="mediwho-card">
                    <SectionTitle label="מיקום ורישום" color="#52c41a" />
                    <Form.Item label="כתובת" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('lecture_address', {
                            initialValue: getSavedFieldValue('lecture_address', lecture ? lecture.lecture_address : '')
                        })(<Input disabled={readonly} placeholder="הכנס כתובת" className="mediwho-lecture-address-input" />)}
                    </Form.Item>
                    <Form.Item label="קישור הרשמה" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('registration_link', {
                            initialValue: getSavedFieldValue('registration_link', lecture ? lecture.registration_link : '')
                        })(<Input disabled={readonly} placeholder="הכנס קישור הרשמה" className="mediwho-lecture-registration-link-input" />)}
                    </Form.Item>
                    <div style={{ textAlign: 'right', marginBottom: hasDigitalLink ? 4 : 12 }}>
                        <Checkbox
                            className="mediwho-lecture-digital-link-checkbox"
                            checked={hasDigitalLink}
                            disabled={readonly}
                            onChange={e => this.setState({ hasDigitalLink: e.target.checked })}
                        >
                            יש קישור דיגיטלי (זום / אונליין)
                        </Checkbox>
                    </div>
                    {hasDigitalLink && (
                        <Form.Item label="קישור דיגיטלי" style={{ marginBottom: 12, marginTop: 6 }}>
                            {getFieldDecorator('digital_link', {
                                initialValue: getSavedFieldValue('digital_link', lecture ? lecture.digital_link : '')
                            })(<Input disabled={readonly} placeholder="הכנס קישור זום / אונליין" className="mediwho-lecture-digital-link-input" />)}
                        </Form.Item>
                    )}
                </div>

                {!readonly && (
                    <div className="mediwho-card mediwho-card-mb8">
                        <SectionTitle label="התראה למשתמשים" color="#d46b08" />
                        {lecture && existingNotification && existingNotification.length > 0 && (
                            <div style={{ textAlign: 'right', padding: '0px 0px', marginBottom: 10, fontSize: 14 }}>
                                {existingNotification.map((n: any, i: number) => (
                                    <div key={i} style={{ fontWeight: 'bold', marginBottom: i < existingNotification.length - 1 ? 4 : 0 }}>
                                        {n.send_at
                                            ? 'כבר נשלחה התראה להרצאה זו בתאריך ' + moment(n.send_at).format('DD/MM/YYYY') + ' בשעה ' + moment(n.send_at).format('HH:mm')
                                            : 'כבר נשלחה התראה להרצאה זו'}
                                    </div>
                                ))}
                            </div>
                        )}
                        <SendNotificationCheckbox
                            className="mediwho-lecture-send-notification-checkbox"
                            checked={sendNotification}
                            onChange={v => this.setState({ sendNotification: v })}
                        />
                    </div>
                )}
            </div>
        )
    }

    render() {
        const { visible, onClose, lecture } = this.props
        const { saving, step, isPast: past } = this.state
        const readonly = past
        const { handleNext, handleBack } = this.wizard

        const title = past ? 'צפייה בהרצאה' : lecture ? 'עריכת הרצאה' : 'הרצאה חדשה'

        const footer: React.ReactNode[] = readonly
            ? [<Button key="close" onClick={onClose}>סגור</Button>]
            : renderWizardFooter({
                step,
                lastStep: 1,
                saving,
                form: this.props.form,
                requiredFieldsByStep: LECTURE_STEP_FIELDS,
                onNext: handleNext,
                onBack: handleBack,
                onSave: this.handleSave,
                onCancel: onClose
            })

        return (
            <Modal
                visible={visible}
                title={title}
                onCancel={onClose}
                footer={footer}
                width={630}
                centered
                wrapClassName="mediwho-app-modal-wrap"
                bodyStyle={{ padding: '24px 24px 8px' }}
            >
                {past && (
                    <Alert
                        type="warning"
                        showIcon
                        message="ההרצאה כבר התקיימה ולא ניתן לערוך אותה"
                        style={{ marginBottom: 16, borderRadius: 8 }}
                    />
                )}
                <Form layout="vertical" className="mediwho-app-modal">
                    {!readonly && <StepBar current={step} steps={LECTURE_STEPS} />}
                    {step === 0 ? this.renderStep0() : this.renderStep1()}
                </Form>
            </Modal>
        )
    }
}

export default Form.create<Props>()(LectureModal)
